import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Box, Fab, Badge } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import CloseIcon from '@mui/icons-material/Close'
import AssistantIcon from '@mui/icons-material/Assistant'
import type { Instance } from '@popperjs/core'
import { makeStyles } from '@mui/styles'
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable'

import AiChatPopper from './ai-chat-popper'
import {
  selectFirstUnreadConversationId,
  selectConversation,
  selectButtonPosition,
  setButtonPosition,
  selectPopperPosition,
  selectUnreadConversationIds,
  selectPopperOpen,
  setPopperOpenState,
} from '@/redux/ducks/ai/chat'
import { useAppDispatch, useAppSelector } from '@/redux/store'

const MotionFab = motion.create(Fab)

const useStyles = makeStyles((theme) => ({
  constraintBox: {
    position: 'fixed',
    inset: 0,
    zIndex: 90,
    pointerEvents: 'none',
    [theme.breakpoints.down(764)]: {
      zIndex: theme.zIndex.appBar + 1,
    },
  },
  iconContainer: {
    display: 'flex',
  },
  draggable: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    pointerEvents: 'auto',
    touchAction: 'none',
    zIndex: 91, // Higher than popper to allow dragging when popper is open
  },
  button: {
    backgroundColor: theme.primaryV2,
    '&:hover': {
      backgroundColor: theme.primary,
    },
  },
}))

export default function AiChatButton() {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const constraintsRef = useRef(null)
  const fabRef = useRef(null)
  const draggableRef = useRef(null)
  const popperRef = useRef<Instance | null>(null)
  const popperPositionSavedRef = useRef(false)
  const pointerStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  const [isDragging, setIsDragging] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const popperOpen = useAppSelector(selectPopperOpen)
  const unreadConversationIds = useAppSelector(selectUnreadConversationIds)

  // Callback ref to set both the ref and state (triggers re-render when mounted)
  const setDraggableRef = useCallback((node: HTMLDivElement | null) => {
    draggableRef.current = node
    setAnchorEl(node)
  }, [])

  const firstUnreadConversationId = useAppSelector(selectFirstUnreadConversationId)
  const buttonPosition = useAppSelector(selectButtonPosition)
  const popperPosition = useAppSelector(selectPopperPosition)

  // Update ref when popperPosition changes
  useEffect(() => {
    popperPositionSavedRef.current = !!popperPosition
  }, [popperPosition])

  // Validate and constrain button position on window resize
  useEffect(() => {
    if (!buttonPosition || !draggableRef.current) return

    const handleResize = () => {
      const element = draggableRef.current
      if (!element) return

      const rect = element.getBoundingClientRect()
      const maxX = window.innerWidth - rect.width
      const maxY = window.innerHeight - rect.height

      const constrainedX = Math.max(0, Math.min(maxX, rect.left))
      const constrainedY = Math.max(0, Math.min(maxY, rect.top))

      // Calculate position offset from default position (bottom-right)
      const defaultX = window.innerWidth - rect.width - 16
      const defaultY = window.innerHeight - rect.height - 16
      const offsetX = constrainedX - defaultX
      const offsetY = constrainedY - defaultY

      if (offsetX !== buttonPosition.x || offsetY !== buttonPosition.y) {
        dispatch(setButtonPosition({ x: offsetX, y: offsetY }))
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [buttonPosition, dispatch])

  // Handler for drag start
  const handleDragStart = () => {
    setIsDragging(true)
  }

  // Handler for drag stop
  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    dispatch(setButtonPosition({ x: data.x, y: data.y }))
    setIsDragging(false)
  }

  const handleOpen = () => {
    const willOpen = !popperOpen
    dispatch(setPopperOpenState(willOpen))

    if (willOpen && firstUnreadConversationId) {
      dispatch(selectConversation(firstUnreadConversationId))
    }
  }

  // Pointer handlers for reliable click detection
  const handlePointerDown = (e: React.PointerEvent) => {
    pointerStartRef.current = { x: e.clientX, y: e.clientY, time: Date.now() }
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!pointerStartRef.current) return

    const { x, y, time } = pointerStartRef.current
    const deltaX = Math.abs(e.clientX - x)
    const deltaY = Math.abs(e.clientY - y)
    const deltaTime = Date.now() - time

    // Consider it a click if: movement < 10px and duration < 300ms
    const isClick = deltaX < 10 && deltaY < 10 && deltaTime < 300

    pointerStartRef.current = null

    if (isClick) {
      handleOpen()
    }
  }

  return (
    <Box
      ref={constraintsRef}
      className={classes.constraintBox}
    >
      <Draggable
        position={buttonPosition || undefined}
        onStart={handleDragStart}
        onStop={handleDragStop}
        bounds="parent"
        nodeRef={draggableRef}
      >
        <div
          ref={setDraggableRef}
          className={classes.draggable}
          data-ai-chat-button="true"
        >
          <Badge
            badgeContent={unreadConversationIds.length}
            color="error"
            max={99}
            overlap="circular"
            invisible={popperOpen || unreadConversationIds.length === 0}
            slotProps={{
              badge: {
                sx: {
                  zIndex: 91,
                },
              },
            }}
          >
            <MotionFab
              color="primary"
              ref={fabRef}
              className={classes.button}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              aria-describedby="ai-chat-popper"
              sx={{ cursor: isDragging ? 'grabbing !important' : 'pointer' }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  className={classes.iconContainer}
                  key={popperOpen ? 'close' : 'add'}
                  initial={{ rotate: -90, opacity: 0, scale: 0.8, display: 'flex' }}
                  animate={{ rotate: 0, opacity: 1, scale: 1 }}
                  exit={{ rotate: 90, opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.22 }}
                >
                  {popperOpen ? <CloseIcon /> : <AssistantIcon />}
                </motion.div>
              </AnimatePresence>
            </MotionFab>
          </Badge>
        </div>
      </Draggable>

      <AiChatPopper
        open={popperOpen}
        anchorEl={anchorEl}
        onClose={() => dispatch(setPopperOpenState(false))}
        popperRef={popperRef}
        blockClickAway={isDragging}
      />
    </Box>
  )
}
